import { useEffect, useRef, useState, type PointerEvent } from "react";

import { PlatformModal } from "../platform-modal";
import styles from "./avatar-upload-modal.module.css";

// The visible crop viewport (a square window onto the source image) and the
// final exported size. OUTPUT_SIZE is deliberately small — this becomes a
// base64 `data:` URL written straight into `silver.lg_member.avatar`
// (services/user/user-service.ts caps that column write at 700kB), so the
// export stays a compressed JPEG well under that ceiling rather than a
// full-resolution photo.
const VIEWPORT = 280;
const OUTPUT_SIZE = 320;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

function clamp(value: number, max: number): number {
  return Math.min(max, Math.max(-max, value));
}

export type AvatarUploadModalProps = {
  file: File;
  onCancel: () => void;
  onConfirm: (dataUrl: string) => void;
};

/**
 * Crop-before-upload modal for the profile photo: pan (drag) + zoom (slider)
 * over the picked image, cropped to a square on `Save`. Hand-rolled canvas
 * crop rather than a cropper dependency — this app's CSS-Modules world
 * stays intentionally light on new deps (see ../routes/profile.module.css's
 * own doc comment on this task's styling convention).
 *
 * `.viewport` itself is clipped to a rounded square (the REAL final avatar
 * shape every avatar in this app — `<NzrAvatar>`, nazare-ui/components —
 * renders as), not a full rectangle: it previews exactly what gets saved.
 * `.circleGuide` is a second, purely decorative thin solid circle on top
 * of that, for framing a face within a classic circular "headshot" safe
 * area; it doesn't crop or mask anything — see handleConfirm, which always
 * reads the full square region regardless of either guide.
 */
export function AvatarUploadModal({ file, onCancel, onConfirm }: AvatarUploadModalProps) {
  // Created INSIDE the effect (not via `useMemo` + a same-deps cleanup-only
  // effect) deliberately: StrictMode's dev-mode double-invoke runs an
  // effect's cleanup then immediately re-runs its setup, so a cleanup-only
  // effect keyed on a `useMemo`'d URL revokes that one and only URL before
  // React re-runs the effect — the `<img>` then tries to decode an
  // already-revoked blob (`complete: true`, `naturalWidth: 0`, confirmed
  // live). Creating the URL as the effect's own setup means the
  // double-invoke's second pass mints a fresh, still-live URL instead.
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const imgRef = useRef<HTMLImageElement>(null);
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; startOffsetX: number; startOffsetY: number } | null>(
    null,
  );

  // Scale that makes the image just cover the viewport at zoom=1 (the
  // shorter-relative-to-viewport dimension is what determines "cover").
  const baseScale = naturalSize ? Math.max(VIEWPORT / naturalSize.width, VIEWPORT / naturalSize.height) : 1;
  const scale = baseScale * zoom;
  const dispWidth = naturalSize ? naturalSize.width * scale : VIEWPORT;
  const dispHeight = naturalSize ? naturalSize.height * scale : VIEWPORT;
  const maxOffsetX = Math.max(0, (dispWidth - VIEWPORT) / 2);
  const maxOffsetY = Math.max(0, (dispHeight - VIEWPORT) / 2);

  // Re-clamp whenever zoom changes the bounds (e.g. zooming out after a
  // drag near the old edge would otherwise leave the image off-center).
  useEffect(() => {
    setOffset((current) => ({
      x: clamp(current.x, maxOffsetX),
      y: clamp(current.y, maxOffsetY),
    }));
  }, [maxOffsetX, maxOffsetY]);

  const handleImageLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
  };

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startOffsetX: offset.x, startOffsetY: offset.y };
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    setOffset({
      x: clamp(drag.startOffsetX + (e.clientX - drag.startX), maxOffsetX),
      y: clamp(drag.startOffsetY + (e.clientY - drag.startY), maxOffsetY),
    });
  };

  const handlePointerUp = () => {
    dragRef.current = null;
  };

  const handleConfirm = () => {
    const img = imgRef.current;
    if (!img || !naturalSize) return;

    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Map the viewport's visible square back into the source image's own
    // pixel coordinates: the image's displayed top-left (in viewport
    // space), then invert the display scale to recover natural-pixel
    // sx/sy/sSize for a source rectangle that exactly fills the viewport.
    const imgLeft = VIEWPORT / 2 - dispWidth / 2 + offset.x;
    const imgTop = VIEWPORT / 2 - dispHeight / 2 + offset.y;
    const inverseScale = 1 / scale;
    const sx = -imgLeft * inverseScale;
    const sy = -imgTop * inverseScale;
    const sSize = VIEWPORT * inverseScale;

    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    onConfirm(canvas.toDataURL("image/jpeg", 0.9));
  };

  return (
    <PlatformModal
      title="Crop photo"
      confirmLabel="Save"
      confirmDisabled={!naturalSize}
      onClose={onCancel}
      onConfirm={() => handleConfirm()}
    >
      <div className={styles.cropBody}>
        <div
          className={styles.viewport}
          style={{ width: VIEWPORT, height: VIEWPORT }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <img
            ref={imgRef}
            src={objectUrl ?? undefined}
            alt=""
            draggable={false}
            onLoad={handleImageLoad}
            className={styles.image}
            style={{
              width: dispWidth,
              height: dispHeight,
              // `-50%,-50%` centres the image's own centre (not its
              // top-left corner) on the viewport's centre; the pan offset
              // then composes on top of that centred rest position.
              transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)`,
            }}
          />
          <div className={styles.circleGuide} aria-hidden="true" />
        </div>

        <input
          type="range"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={0.01}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          className={styles.zoomSlider}
          aria-label="Zoom"
          disabled={!naturalSize}
        />
      </div>
    </PlatformModal>
  );
}
